import type { ReactElement } from 'react';
import React, { useContext } from 'react';
import { Modal } from '../ui';
import type { InitialOrderValues, OnSaveType } from './OrderForm';
import OrderForm from './OrderForm';
import { useCreateOrderWithRelationsMutation } from './order';
import { toast } from 'react-toastify';
import { ModalContext } from '../contexts/ModalContext';
import { useEditOrderModalQuery } from '../../graphql';
import omit from 'lodash/omit';
import type { ModalProps } from '../ui/Modal/Modal';

export interface NewOrderModalProps extends ModalProps {
  onCreate?: () => void;
  duplicateFromOrderId?: number;
  currentUserOrg?: number;
  onSaveAndCloseOrder?: (args: { orderIds: number[] }) => void;
}

const NewOrderModal = ({
  onClose,
  onCreate,
  duplicateFromOrderId,
  currentUserOrg,
  onSaveAndCloseOrder,
}: NewOrderModalProps): ReactElement => {
  const [createOrderWithRelationsMutation, { loading: createLoading }] =
    useCreateOrderWithRelationsMutation();

  const { data: orderToDuplicate, loading: orderLoading } =
    useEditOrderModalQuery({
      variables: {
        orderId: duplicateFromOrderId ?? NaN,
      },
      skip: duplicateFromOrderId === undefined,
    });

  // Take initial values from order to duplicate but remove references to the actual data id's so that new rows are created
  const initialValues: InitialOrderValues | undefined =
    orderToDuplicate?.orderById
      ? {
          ...orderToDuplicate.orderById,
          id: undefined,
          externalOrderId: undefined,
          orderDetailsByOrderId: {
            nodes: (
              orderToDuplicate.orderById.orderDetailsByOrderId.nodes ?? []
            ).map((x) => ({
              ...x,
              id: undefined,
              orderName: undefined,
              orderId: undefined,
            })),
          },
          cargoExclusionsByOrderId: {
            nodes: (
              orderToDuplicate.orderById.cargoExclusionsByOrderId.nodes ?? []
            ).map((x) => ({
              ...x,
              id: undefined,
              orderId: undefined,
            })),
          },
          orderRequirementAssociationsByOrderId: {
            nodes: (
              orderToDuplicate.orderById.orderRequirementAssociationsByOrderId
                .nodes ?? []
            ).map((x) => ({
              ...x,
              id: undefined,
              orderId: undefined,
            })),
          },
          orderAttributeAssociationsByOrderId: {
            nodes: (
              orderToDuplicate.orderById.orderAttributeAssociationsByOrderId
                .nodes ?? []
            ).map((x) => ({
              ...x,
              id: undefined,
              orderId: undefined,
            })),
          },
        }
      : undefined;

  const modalContext = useContext(ModalContext);

  const onSave: OnSaveType = async (
    {
      order,
      orderDetail,
      cargoExclusions,
      specialRequirements,
      orderAttributes,
    },
    opts = {},
  ) => {
    try {
      const res = await createOrderWithRelationsMutation(
        {
          ...omit(order, 'id'),
        },
        omit(orderDetail, 'id'),
        cargoExclusions.map((excl) => omit(excl, 'id')),
        specialRequirements.map((req) => omit(req, 'id')),
        orderAttributes.map((attr) => omit(attr, 'id')),
      );

      const [createOrderRes] = res.data ?? [];
      const createdOrder = createOrderRes?.createOrder?.order ?? null;

      if (!createdOrder) {
        toast.error(`Could not retrieve created order`);
        const errMsg = 'createdOrder missing';
        console.error(errMsg);
        return [new Error(errMsg)];
      }

      toast.success(`Order created.`);

      if (onCreate) {
        onCreate();
      }

      // always close modal and decide whether to open edit modal or not
      onClose();
      if (!opts.shouldClose) {
        modalContext.openModal({
          type: 'VIEW_ORDER',
          props: { orderId: createdOrder.id },
        });
      } else {
        onSaveAndCloseOrder?.({
          orderIds: [createdOrder.id],
        });
      }

      if (opts.createAnother) {
        modalContext.openModal({
          type: 'CREATE_ORDER',
          props: { onCreate, currentUserOrg },
        });
      }
    } catch (err) {
      // TODO: this is semi-temporary. User should not be shown database generated error messages.
      console.error('Error saving new order', err.message);
      toast.error(
        `Could not save order or its associated details: ${err.message}`,
      );
      return [err];
    }
    return [];
  };

  return (
    <Modal isOpen onClose={onClose}>
      {orderLoading && <div style={{ color: 'white' }}>Loading...</div>}
      {!orderLoading && (
        <OrderForm
          isAssigned={false}
          onSave={onSave}
          onCancel={onClose}
          isLoading={createLoading}
          initialValues={initialValues}
        />
      )}
    </Modal>
  );
};

export default NewOrderModal;
